import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  X,
  ShoppingBag,
  Boxes,
  Receipt,
  FileText,
  LayoutDashboard,
} from 'lucide-react';

import AuthContext from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import { devLog } from '../utils/devLog';
import { isSiteOnly } from '../utils/businessConfig';

const STATS_POLL_MS = 60000;

// Module-level cache for the quick-stats response. Base.js renders per page, so
// this FAB remounts on every navigation and previously refetched
// /dashboard/quick-stats/ each time. Reuse a fresh-enough value on remount
// instead of hitting the server again; the 60s poll still keeps it current.
const STATS_TTL_MS = 60000;
let statsCache = null; // { ts, data }

const ACTIONS = [
  { id: 'add_order', label: 'Add Order', icon: ShoppingBag, route: '/orders-unified?quickadd=1', color: '#B40A0A' },
  { id: 'check_inventory', label: 'Check Inventory', icon: Boxes, route: '/inventory', color: '#0ea5e9' },
  { id: 'add_expense', label: 'Add Expense', icon: Receipt, route: '/expenses?new=1', color: '#ef4444' },
  { id: 'create_bill', label: 'Create Bill', icon: FileText, route: '/payments?collect=1', color: '#10b981' },
  { id: 'view_dashboard', label: 'View Dashboard', icon: LayoutDashboard, route: '/dashboard', color: '#8b5cf6' },
];

/**
 * QuickActionFAB
 * Site-wide floating action button that expands to a column of quick actions.
 * Polls /api/dashboard/quick-stats/ every 60s for an action-item badge on the main button.
 */
const QuickActionFAB = () => {
  const { authToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState({ action_items: 0 });
  const containerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Poll quick stats
  useEffect(() => {
    if (!authToken?.access) return undefined;
    let cancelled = false;

    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/dashboard/quick-stats/`, {
          headers: { Authorization: `Bearer ${authToken.access}` },
        });
        const data = res.data || { action_items: 0 };
        statsCache = { ts: Date.now(), data };
        if (!cancelled && mountedRef.current) {
          setStats(data);
        }
      } catch (err) {
        devLog.error('Quick stats poll failed:', err);
      }
    };

    // Seed from a fresh-enough cache on remount and skip the immediate refetch;
    // otherwise fetch right away. The interval keeps it current either way.
    if (statsCache && Date.now() - statsCache.ts < STATS_TTL_MS) {
      setStats(statsCache.data);
    } else {
      fetchStats();
    }
    const id = setInterval(fetchStats, STATS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [authToken?.access]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  const handleAction = (action) => {
    setOpen(false);
    if (!action?.route) return;
    if (/^https?:\/\//i.test(action.route)) {
      window.open(action.route, '_blank', 'noopener,noreferrer');
    } else {
      navigate(action.route);
    }
  };

  // Don't render until authed — avoids leaking buttons on login/landing.
  // A site-only (portfolio) account has none of these actions: every one is
  // an order/stock/expense shortcut, so the FAB simply doesn't exist for it.
  if (!authToken?.access || isSiteOnly()) return null;

  const actionCount = Number(stats?.action_items || 0);

  return (
    <div
      ref={containerRef}
      // One tight corner stack with the WhatsApp float below it (Base.js) —
      // the old lg:bottom-24 lifted this 96px up the page, where it sat on
      // top of stat values, table cells and buttons on every scrolled page.
      className="fixed bottom-[136px] right-4 lg:bottom-[76px] lg:right-5 z-40 flex flex-col items-end"
    >
      <AnimatePresence>
        {open && (
          <motion.ul
            key="fab-menu"
            initial="closed"
            animate="open"
            exit="closed"
            variants={{
              open: { transition: { staggerChildren: 0.04, delayChildren: 0.03 } },
              closed: { transition: { staggerChildren: 0.02, staggerDirection: -1 } },
            }}
            className="mb-3 flex flex-col items-end gap-2"
          >
            {ACTIONS.map((action) => {
              const Icon = action.icon;
              return (
                <motion.li
                  key={action.id}
                  variants={{
                    open: { opacity: 1, y: 0, scale: 1 },
                    closed: { opacity: 0, y: 8, scale: 0.9 },
                  }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                  className="flex items-center gap-2"
                >
                  <span className="px-2.5 py-1 rounded-lg bg-gray-900/90 text-white text-xs font-medium shadow-md whitespace-nowrap">
                    {action.label}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleAction(action)}
                    aria-label={action.label}
                    title={action.label}
                    className="w-11 h-11 rounded-full flex items-center justify-center text-white shadow-lg hover:scale-110 transition-transform"
                    style={{ backgroundColor: action.color }}
                  >
                    <Icon size={18} />
                  </button>
                </motion.li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close quick actions' : 'Open quick actions'}
        aria-expanded={open}
        whileTap={{ scale: 0.92 }}
        title={actionCount > 0
          ? `Quick actions — ${actionCount} thing${actionCount === 1 ? '' : 's'} need${actionCount === 1 ? 's' : ''} your attention today`
          : 'Quick actions'}
        className="relative w-12 h-12 rounded-full flex items-center justify-center text-white shadow-xl transition-all duration-300"
        style={{ backgroundColor: '#B40A0A' }}
      >
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex items-center justify-center"
        >
          {open ? <X size={20} /> : <Plus size={20} />}
        </motion.span>
        {!open && actionCount > 0 && (
          <span
            title={`${actionCount} thing${actionCount === 1 ? '' : 's'} need${actionCount === 1 ? 's' : ''} your attention today`}
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
          >
            {actionCount > 9 ? '9+' : actionCount}
          </span>
        )}
      </motion.button>
    </div>
  );
};

export default QuickActionFAB;
